// ── Product tour: a short walk through every screen a staff member can open ──
// The sidebar publishes what this person actually sees (role, edition and
// feature filtering already applied, groups as they appear) and their role,
// and the tour turns it into steps: a welcome that says what the role does,
// one step per sidebar row or group, and a closing step. A screen's line is
// the role's own where the role uses it differently (a server takes no
// payments, an accountant only reads). Each step opens its screen and
// highlights its sidebar row (data-tour). Pure, so the rules are tested on
// their own.

export interface TourLink {
  id: string
  label: string
  href: string
  /** The sidebar's own description: the fallback text for a section the tour has no line for. */
  description: string
  /** A line for this row only, when it differs from the section's usual one. */
  line?: string
}

export type TourNode = { kind: 'item'; item: TourLink } | { kind: 'group'; id: string; label: string; items: TourLink[] }

export interface TourLine {
  /** Set on a group step: the screen the line is about. */
  label?: string
  text: string
}

export interface TourStep {
  key: string
  title: string
  lines: TourLine[]
  /** The screen the step opens. */
  href?: string
  /** The sidebar row to highlight (its data-tour id). */
  target?: string
}

/** One brief line per sidebar section, in the words the screens use. */
export const TOUR_LINES: Record<string, string> = {
  dashboard: "Today at a glance: sales, orders, the daily brief and anything that needs attention.",
  'bhookly-ai':
    'Ask anything by typing or speaking, in English or Urdu. It answers with your figures, opens screens and prepares menu changes for you to confirm.',
  counter: 'Take orders and payments: add items, pick the order type, send to the kitchen and charge.',
  server: "Waiters' ordering screen: open a table, add items and fire them to the kitchen.",
  'online-orders': 'Orders from your website arrive here to accept or reject.',
  'day-close': 'Start the day, count the cash and finish the day. The dot shows whether today is still open.',
  dispatch: 'Assign riders to deliveries, follow them on the way, and take the cash they bring back.',
  rider: 'Your delivery queue: the orders assigned to you.',
  website: 'Your ordering website: theme, colours, photos and hours.',
  'delivery-zones': 'Where you deliver, with the charge and minimum order for each area.',
  menu: 'Products, categories, prices and options, plus Costing & recipes.',
  tables: 'Your dining tables and floor areas.',
  kitchen: 'The kitchen display: tickets as they come in, from preparing to ready.',
  stations: 'Which kitchen station gets which items.',
  production: 'Ask the store for stock, receive it and send returns.',
  catering: 'Event bookings, payments and invoices.',
  inventory: 'Stock items, vendors and purchasing. Low stock shows under Alerts.',
  customers: 'Your guest list: profiles, visits and spend.',
  marketing: 'Loyalty, promo codes, campaigns, QR codes and segments to bring guests back.',
  reports: 'Sales, items, payments, tax and day-close reports for any dates.',
  expenses: 'Record expenses and see profit and loss.',
  receipts: 'Scan receipts and review what was read.',
  'void-log': 'Every voided item, who voided it and who approved it.',
  staff: 'Staff accounts, roles and PINs.',
  hr: 'Attendance, leave, advances and salaries.',
  settings: 'Restaurant details, tax, receipts and printing, order types and kitchen setup.',
}

/** What each role does, in one line: the tour's welcome. */
export const ROLE_INTROS: Record<string, string> = {
  admin: 'You see everything: sales and the menu, stock, marketing, staff and settings.',
  manager: 'You run the shift: the till, the menu and tables, staff, daily closing and the reports.',
  inventory_manager: 'You look after stock: items, vendors, purchasing and counts.',
  counter: 'You take orders and payments at the counter, and handle online orders.',
  server: 'You take orders at the tables and send them to the kitchen.',
  kitchen: 'You cook from the tickets on the kitchen display.',
  kitchen_manager: 'You run the kitchen: production, stock requests, the kitchen display and catering.',
  accountant: 'You check the numbers: reports, expenses and catering. Your screens are read-only.',
  rider: 'You deliver orders: your queue shows each delivery assigned to you.',
}

/** A role's own line for a screen it uses differently from the owner. */
export const ROLE_LINES: Record<string, Record<string, string>> = {
  manager: {
    staff: 'Staff accounts, roles and PINs. Admin accounts are managed by the owner.',
    expenses: 'Record and edit expenses. The profit and loss is the owner\'s view.',
  },
  counter: {
    counter: 'Your main screen: add items, pick dine-in, takeaway or delivery, send to the kitchen and take payment.',
    server: 'The floor view: open a table and add items to it.',
  },
  server: {
    server: 'Your screen: pick a table, add items and fire them to the kitchen. Payment is taken at the counter.',
  },
  kitchen: {
    kitchen: 'Your tickets, as they are fired: move each one from preparing to ready.',
  },
  kitchen_manager: {
    production: 'Request stock from the store, receive it, send returns and run production batches.',
  },
  accountant: {
    reports: 'Sales, items, payments, tax and day-close reports for any dates, ready to export.',
    expenses: 'The expense ledger and the profit and loss, to read and export.',
    catering: 'Event bookings and their payments, read-only.',
  },
}

function lineFor(link: TourLink, role: string): string {
  return link.line ?? ROLE_LINES[role]?.[link.id] ?? TOUR_LINES[link.id] ?? link.description
}

function hasItem(nav: TourNode[], id: string): boolean {
  return nav.some((n) => (n.kind === 'item' ? n.item.id === id : n.items.some((i) => i.id === id)))
}

/**
 * The tour for what this staff member's sidebar shows, in their role's words.
 * Empty when the sidebar shows nothing.
 */
export function buildTourSteps(nav: TourNode[], role: string): TourStep[] {
  if (nav.length === 0) return []
  const intro: TourLine[] = [
    { text: 'Each step opens a screen and points to it in the menu on the left. Use Next and Back, or press Esc to stop.' },
  ]
  if (ROLE_INTROS[role]) intro.unshift({ text: ROLE_INTROS[role] })
  const steps: TourStep[] = [{ key: 'intro', title: 'Welcome to Bhookly', lines: intro }]
  for (const n of nav) {
    if (n.kind === 'item') {
      steps.push({ key: n.item.id, title: n.item.label, lines: [{ text: lineFor(n.item, role) }], href: n.item.href, target: n.item.id })
    } else {
      steps.push({
        key: n.id,
        title: n.label,
        lines: n.items.map((i) => ({ label: i.label, text: lineFor(i, role) })),
        href: n.items[0]?.href,
        target: n.id,
      })
    }
  }
  const again = hasItem(nav, 'settings')
    ? 'Take this tour again any time from Settings or the menu under your name.'
    : 'Take this tour again any time from the menu under your name.'
  steps.push({
    key: 'outro',
    title: "You're all set",
    lines: hasItem(nav, 'bhookly-ai') ? [{ text: 'Stuck on something? Ask Bhookly AI.' }, { text: again }] : [{ text: again }],
  })
  return steps
}
